/**
 * Pure data derivations for the /dashboard surface, on top of the calendar listing (ADR 0008).
 * Everything the dashboard renders from one fetched window is decided here, deterministically
 * on an injected `now`: today's meetings, the next upcoming event, the month-widget event marks
 * and the derived notification feed. Dates stay in local time exactly like the calendar module.
 */

import type { CalendarEvent } from '../calendar/api';
import {
  DAY_MS,
  eventCountByDay,
  eventEnd,
  eventStart,
  nextUpcoming,
  sameDay,
  startOfDay,
} from '../calendar/month';

/**
 * The fetch window: the displayed month's span widened to always cover today-7d..today+7d, so
 * one listEvents call feeds the today list, the upcoming card and the month widget together
 * (the same window shape the /calendar page fetches).
 */
export function dataWindow(now: Date, month: Date): { readonly from: Date; readonly to: Date } {
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0, 23, 59, 59);
  const listStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
  const listEnd = new Date(now.getTime() + 7 * DAY_MS);
  return {
    from: monthStart < listStart ? monthStart : listStart,
    to: monthEnd > listEnd ? monthEnd : listEnd,
  };
}

/** Today's events ordered by their occurrence start (all-day first is the listing's order). */
export function todaysEvents(events: readonly CalendarEvent[], now: Date): CalendarEvent[] {
  const day = startOfDay(now);
  return events
    .filter((event) => sameDay(eventStart(event), day))
    .sort((a, b) => eventStart(a).getTime() - eventStart(b).getTime());
}

/**
 * The next upcoming event (not yet over), or null when every event has ended. Delegates to the
 * calendar module's pick so the dashboard and the /calendar page never disagree.
 */
export function nearestEvent(events: readonly CalendarEvent[], now: Date): CalendarEvent | null {
  return nextUpcoming(events, now);
}

/** Local days of the displayed month carrying at least one event (yyyy-mm-dd keys). */
export function monthEventDays(
  events: readonly CalendarEvent[],
  year: number,
  monthIndex: number,
): ReadonlySet<string> {
  return new Set(eventCountByDay(events, year, monthIndex).keys());
}

/** Notification kinds the dashboard derives from the listing. */
export type DashboardNotification =
  | { readonly kind: 'live'; readonly event: CalendarEvent }
  | { readonly kind: 'soon'; readonly event: CalendarEvent; readonly startsInMs: number };

/** How far ahead a starting event is announced on the dashboard. */
export const SOON_WINDOW_MS = 60 * 60 * 1000;

/**
 * The notification feed, derived from the fetched window: events running right now first,
 * then events starting within the next hour, each list ordered by start. No persistent
 * notification store exists yet - this keeps the section real instead of mocked.
 */
export function deriveNotifications(
  events: readonly CalendarEvent[],
  now: Date,
): DashboardNotification[] {
  const at = now.getTime();
  const live: DashboardNotification[] = [];
  const soon: DashboardNotification[] = [];
  for (const event of events) {
    const start = eventStart(event).getTime();
    const end = eventEnd(event).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) {
      continue;
    }
    if (start <= at && at <= end) {
      live.push({ kind: 'live', event });
      continue;
    }
    const untilStart = start - at;
    if (untilStart > 0 && untilStart <= SOON_WINDOW_MS) {
      soon.push({ kind: 'soon', event, startsInMs: untilStart });
    }
  }
  live.sort((a, b) => eventStart(a.event).getTime() - eventStart(b.event).getTime());
  soon.sort((a, b) => eventStart(a.event).getTime() - eventStart(b.event).getTime());
  return [...live, ...soon];
}
